import classNames from "classnames";
import compact from "lodash/compact";
import { Inline } from "PFComponents/containers";
import { LabelIcon, LabelIconConfig } from "PFComponents/label/label_icon";
import { Typography, TypographyProps } from "PFComponents/typography";
import { Variant, VariantHeader } from "PFComponents/typography/typography.types";
import { useTranslation } from "react-i18next";

import css from "./label.module.scss";

export type LabelIcons = LabelIconConfig[];

type LabelProps = Pick<TypographyProps, "withMargin" | "id"> & {
  text?: string | null;
  icons?: LabelIcons;
  required?: boolean;
  variant?: Variant;
  inputId?: string;
  disabled?: boolean;
  className?: string;
  qaId?: string;
};

const isHeaderVariant = (variant: Variant): variant is VariantHeader => /^h[1-6]$/.test(variant);

export const Label = ({
  text,
  icons = [],
  required,
  variant = "labelRegular",
  inputId,
  disabled,
  qaId,
  className,
  withMargin,
  id
}: LabelProps) => {
  const { t } = useTranslation("core");

  const mandatoryIcon =
    required &&
    ({
      icon: "mandatory",
      text: t("components.requiredText.mandatory")
    } as LabelIconConfig);

  if (!text) {
    return null;
  }
  const commonProps = {
    title: text,
    clipOverflow: true,
    withMargin
  };

  return (
    <Inline<"label">
      id={id}
      alignItems="center"
      gap="spacingSm"
      as={inputId ? "label" : "span"}
      htmlFor={inputId}
      className={classNames(css.label, disabled && css.disabled, className)}
      title={text}
      data-qa-id={qaId}
    >
      {isHeaderVariant(variant) ? (
        <Typography {...commonProps} variant={variant}>
          {text}
        </Typography>
      ) : (
        <Typography {...commonProps} variant={variant} tag="span">
          {text}
        </Typography>
      )}
      {compact([...icons, mandatoryIcon]).map((iconConfig) => (
        <LabelIcon key={iconConfig.icon} {...iconConfig} disabled={disabled} />
      ))}
    </Inline>
  );
};
